/**
 * Returns true if the value is an object which has a property with the given
 * name with any value including `undefined`.
 */
export function hasKey<Property extends PropertyKey>(
    value: unknown,
    name: Property,
): value is Record<Property, unknown> {
    return typeof value === 'object' && value !== null && name in value;
}

/**
 * Represents all types except `undefined`.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- this is intended
export type NotUndefined = {} | null;

/**
 * Returns true if the value is an object which has a property with the given
 * name with a value other than `undefined`.
 */
export function hasProperty<Property extends PropertyKey>(
    value: unknown,
    name: Property,
): value is Record<Property, NotUndefined> {
    return hasKey(value, name) && value[name] !== undefined;
}

export function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function isEmpty(value: unknown): boolean {
    return (
        value === null ||
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && isEmptyObject(value))
    );
}

export function isEmptyObject(value: object, isEmptyValue = isEmpty): boolean {
    return !Object.values(value).some((x) => !isEmptyValue(x));
}

export function omitEmpty<T extends object>(obj: T): Partial<T> {
    /* eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion 
    -- needed for object wrangling */
    return Object.fromEntries(Object.entries(obj).filter(([, v]) => !isEmpty(v))) as Partial<T>;
}
